import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import DashboardSection from '@/pages/admin/sections/DashboardSection';
import CourseManagerSection from '@/pages/admin/sections/CourseManagerSection';
import PricingControlSection from '@/pages/admin/sections/PricingControlSection';
import SiteContentSection from '@/pages/admin/sections/SiteContentSection';
import LeadsEnquiriesSection from '@/pages/admin/sections/LeadsEnquiriesSection';
import EnrolmentsSection from '@/pages/admin/sections/EnrolmentsSection';
import AnalyticsSection from '@/pages/admin/sections/AnalyticsSection';
import SecurityCentreSection from '@/pages/admin/sections/SecurityCentreSection';
import { useSEO } from '@/hooks/useSEO';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();
import {
  LayoutDashboard,
  BookOpen,
  Tag,
  FileText,
  Inbox,
  GraduationCap,
  BarChart3,
  ShieldCheck,
  Shield,
  LogOut,
  Circle,
} from 'lucide-react';

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes

const SECTIONS = [
  { id: 'dashboard',         title: 'Dashboard',         icon: LayoutDashboard, lead: 'Admin overview and platform KPIs.' },
  { id: 'course-manager',    title: 'Course Manager',    icon: BookOpen,        lead: 'Create, edit, and publish courses.' },
  { id: 'pricing-control',   title: 'Pricing Control',   icon: Tag,             lead: 'Manage pricing tiers and promotions.' },
  { id: 'site-content',      title: 'Site Content',      icon: FileText,        lead: 'Homepage sections, copy, and assets.' },
  { id: 'leads-enquiries',   title: 'Leads & Enquiries', icon: Inbox,           lead: 'Inbound contact form submissions.' },
  { id: 'enrolments',        title: 'Enrolments',        icon: GraduationCap,   lead: 'Active and historical course enrolments.' },
  { id: 'analytics',         title: 'Analytics',         icon: BarChart3,       lead: 'Traffic, conversion, and engagement metrics.' },
  { id: 'security-centre',   title: 'Security Centre',   icon: ShieldCheck,     lead: 'Admin audit trail and access controls.' },
];

export default function AdminShell() {
  useSEO({
    title: 'Admin Console | Acodia',
    description: 'Acodia administrator console — courses, pricing, leads, analytics, security.',
    path: '/admin',
    robots: 'noindex,nofollow',
  });
  const { admin, logout, adminFetch } = useAdminAuth();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState('dashboard');
  const [newLeadCount, setNewLeadCount] = useState(0);
  const idleTimer = useRef(null);

  const refreshLeadCounts = async () => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/leads/counts`);
      if (res.ok) {
        const { summary } = await res.json();
        setNewLeadCount(summary?.new ?? 0);
      }
    } catch { /* badge is best-effort — ignore */ }
  };

  useEffect(() => { refreshLeadCounts(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  useEffect(() => {
    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        handleLogout();
      }, INACTIVITY_MS);
    };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = SECTIONS.find((s) => s.id === activeId) || SECTIONS[0];

  return (
    <div
      data-testid="admin-shell"
      className="min-h-screen"
      style={{ background: 'var(--bg)', color: 'var(--white)' }}
    >
      {/* Sidebar — 240px */}
      <aside
        data-testid="admin-sidebar"
        className="fixed top-0 left-0 h-screen flex flex-col"
        style={{
          width: 240,
          background: 'var(--l1)',
          borderRight: '1px solid var(--b1)',
        }}
      >
        <div className="flex items-center gap-2 px-5" style={{ height: 52, borderBottom: '1px solid var(--b1)' }}>
          <img
            src="/images/logo/logo-admin-28.png"
            alt="Acodia"
            style={{ height: 24, width: 'auto', display: 'block' }}
          />
          <span
            className="font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded"
            style={{ color: 'var(--a-bright)', background: 'var(--a-glow)', border: '1px solid var(--a-border)' }}
          >
            admin
          </span>
        </div>

        <nav className="flex-1 overflow-y-auto py-3" data-testid="admin-nav">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = s.id === activeId;
            const showBadge = s.id === 'leads-enquiries' && newLeadCount > 0;
            return (
              <button
                key={s.id}
                data-testid={`admin-nav-${s.id}`}
                onClick={() => setActiveId(s.id)}
                className="w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors"
                style={{
                  color: isActive ? 'var(--white)' : 'var(--tm)',
                  background: isActive ? 'var(--a-glow)' : 'transparent',
                  borderLeft: isActive ? '2px solid var(--a)' : '2px solid transparent',
                }}
              >
                <Icon size={14} style={{ color: isActive ? 'var(--a)' : 'var(--ts)' }} />
                <span className="font-body text-sm flex-1">{s.title}</span>
                {showBadge && (
                  <span
                    data-testid="admin-nav-leads-badge"
                    className="font-mono text-[10px] px-1.5 py-0.5 rounded-full"
                    style={{
                      background: 'var(--danger)',
                      color: 'var(--white)',
                      minWidth: 20,
                      textAlign: 'center',
                      lineHeight: 1,
                    }}
                  >
                    {newLeadCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="px-5 py-3" style={{ borderTop: '1px solid var(--b1)' }}>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--te)' }}>
            signed in as
          </p>
          <p className="font-body text-xs truncate" style={{ color: 'var(--tm)' }} title={admin?.email}>
            {admin?.email || '—'}
          </p>
        </div>
      </aside>

      {/* Top bar — 52px */}
      <header
        data-testid="admin-topbar"
        className="fixed top-0 right-0 flex items-center justify-between px-6"
        style={{
          left: 240,
          height: 52,
          background: 'var(--l1)',
          borderBottom: '1px solid var(--b1)',
          zIndex: 5,
        }}
      >
        <h1
          data-testid="admin-section-title"
          className="font-display text-base font-semibold"
          style={{ color: 'var(--white)' }}
        >
          {active.title}
        </h1>

        <div className="flex items-center gap-4">
          <div
            data-testid="admin-system-status"
            className="flex items-center gap-2 px-3 py-1 rounded-full"
            style={{
              background: 'rgba(74,222,128,0.08)',
              border: '1px solid rgba(74,222,128,0.25)',
            }}
          >
            <Circle
              size={8}
              fill="var(--ok)"
              style={{
                color: 'var(--ok)',
                animation: 'pulseDot 2s ease-in-out infinite',
              }}
            />
            <span className="font-mono text-[11px] tracking-wide" style={{ color: 'var(--ok)' }}>
              All Systems Operational
            </span>
          </div>

          <span
            data-testid="admin-badge"
            className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded"
            style={{ color: 'var(--a-bright)', background: 'var(--a-glow)', border: '1px solid var(--a-border)' }}
          >
            administrator
          </span>

          <button
            data-testid="admin-signout"
            onClick={handleLogout}
            className="flex items-center gap-1.5 font-body text-xs px-3 py-1.5 rounded-[3px] transition-colors"
            style={{ color: 'var(--tm)', border: '1px solid var(--b1)', background: 'transparent' }}
          >
            <LogOut size={12} />
            Sign out
          </button>
        </div>
      </header>

      {/* Toast container — scoped to admin area */}
      <Toaster
        theme="dark"
        position="top-right"
        richColors
        toastOptions={{
          style: {
            background: 'var(--l2)',
            border: '1px solid var(--b2)',
            color: 'var(--white)',
            fontFamily: 'var(--fb)',
          },
        }}
      />

      {/* Main content */}
      <main
        className="pt-[52px]"
        style={{ marginLeft: 240, minHeight: '100vh' }}
      >
        <div className="p-8" data-testid="admin-main">
          <div data-testid={`admin-panel-${active.id}`}>
            {active.id === 'dashboard' ? (
              <DashboardSection />
            ) : active.id === 'course-manager' ? (
              <CourseManagerSection />
            ) : active.id === 'pricing-control' ? (
              <PricingControlSection />
            ) : active.id === 'site-content' ? (
              <SiteContentSection />
            ) : active.id === 'leads-enquiries' ? (
              <LeadsEnquiriesSection onDataChanged={refreshLeadCounts} />
            ) : active.id === 'enrolments' ? (
              <EnrolmentsSection />
            ) : active.id === 'analytics' ? (
              <AnalyticsSection />
            ) : active.id === 'security-centre' ? (
              <SecurityCentreSection />
            ) : (
              <div
                className="rounded-lg p-8"
                style={{ background: 'var(--l1)', border: '1px solid var(--b1)' }}
              >
                <p
                  className="font-mono text-[10px] uppercase tracking-[0.25em] mb-2"
                  style={{ color: 'var(--a)' }}
                >
                  section · {active.id}
                </p>
                <h2 className="font-display text-2xl font-bold mb-2" style={{ color: 'var(--white)' }}>
                  {active.title}
                </h2>
                <p className="font-body text-sm mb-6" style={{ color: 'var(--tm)' }}>
                  {active.lead}
                </p>

                <div
                  className="rounded border-dashed flex items-center justify-center"
                  style={{
                    minHeight: 240,
                    border: '1px dashed var(--b2)',
                    background: 'rgba(139,92,246,0.03)',
                  }}
                >
                  <div className="text-center">
                    <p
                      className="font-mono text-xs uppercase tracking-[0.25em] mb-1"
                      style={{ color: 'var(--ts)' }}
                    >
                      placeholder
                    </p>
                    <p className="font-body text-sm" style={{ color: 'var(--te)' }}>
                      {active.title} controls will be built in a later Phase 7 step.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
